import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { DECK_CAPACITY, NAVY, GOLD } from '../utils/constants';
import { useCruiseSettings } from '../hooks/useCruiseSettings';
import { getCruiseClosures } from '../utils/closures';
import AvailabilityStrip from './AvailabilityStrip';
import { Mail, Anchor } from 'lucide-react';
import { eventsOnDay, typeLabel } from './Events';

// ─── Date helpers ────────────────────────────────────────────────────────────

const toDisplayDate = (yyyymmdd) => {
  if (!yyyymmdd) return '';
  const [y, m, d] = yyyymmdd.split('-');
  return `${d}-${m}-${y}`;
};

const todayInput = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const ordinal = (n) => { const s = ['th','st','nd','rd'], v = n % 100; return n + (s[(v-20)%10] || s[v] || s[0]); };
const formatDateWords = (yyyymmdd) => {
  if (!yyyymmdd) return '';
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  return `${ordinal(d)} ${MONTHS[m - 1]} ${y}`;
};
const shortDate = (yyyymmdd) => {
  if (!yyyymmdd) return '';
  const [, m, d] = yyyymmdd.split('-').map(Number);
  return `${d} ${MONTHS[m - 1].slice(0, 3)}`;
};
const getMondayOf = (yyyymmdd) => {
  const d = new Date(yyyymmdd + 'T00:00:00');
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};
const generateDisplayDates = (from, to) => {
  const dates = [], cur = new Date(from + 'T00:00:00'), end = new Date(to + 'T00:00:00');
  while (cur <= end) {
    dates.push(toDisplayDate(`${cur.getFullYear()}-${String(cur.getMonth()+1).padStart(2,'0')}-${String(cur.getDate()).padStart(2,'0')}`));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
};
// Same range as generateDisplayDates but in YYYY-MM-DD (daily_adjustments.date format)
const generateInputDates = (from, to) => {
  const dates = [], cur = new Date(from + 'T00:00:00'), end = new Date(to + 'T00:00:00');
  while (cur <= end) {
    dates.push(`${cur.getFullYear()}-${String(cur.getMonth()+1).padStart(2,'0')}-${String(cur.getDate()).padStart(2,'0')}`);
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
};
const isPastOrToday = (ddmmyyyy, iDate) => {
  if (!ddmmyyyy) return false;
  const [d, m, y] = ddmmyyyy.split('-').map(Number);
  return new Date(y, m - 1, d) <= new Date(iDate + 'T00:00:00');
};
const fmt = (n) => (n || 0).toLocaleString('en-IN');
const daysSince = (iso) => Math.floor((new Date() - new Date(iso)) / 86400000);

// ─── Capacity colour coding ──────────────────────────────────────────────────

const capacityColor = (pax, max) => {
  const r = pax / max;
  if (r >= 0.9) return { bar: '#ef4444', barEnd: '#dc2626', label: '#991b1b', bg: '#fff1f2', border: '#fca5a5', leftBar: '#ef4444' };
  if (r >= 0.6) return { bar: '#f59e0b', barEnd: '#d97706', label: '#92400e', bg: '#fffbeb', border: '#fcd34d', leftBar: '#f59e0b' };
  if (r > 0)   return { bar: '#22c55e', barEnd: '#16a34a', label: '#15803d', bg: '#f0fdf4', border: '#86efac', leftBar: '#22c55e' };
  return             { bar: '#d1d5db', barEnd: '#9ca3af', label: '#6b7280', bg: '#f9fafb', border: '#e5e7eb', leftBar: '#d1d5db' };
};

// ─── Component ───────────────────────────────────────────────────────────────

export default function Home({ onNavigate }) {
  const [inputDate, setInputDate]         = useState(todayInput);
  const [capacityData, setCapacityData]   = useState({});
  const [dayEvents, setDayEvents]         = useState([]);
  const [loading, setLoading]             = useState(false);
  const [draftCount, setDraftCount]       = useState(0);
  const [hoveredCard, setHoveredCard]     = useState(null);
  const [showAllPartners, setShowAllPartners] = useState(false);
  const [recentBookings, setRecentBookings]   = useState([]);
  const [mobileTab, setMobileTab]             = useState('capacity');
  const [closures, setClosures]               = useState({}); // { [cruise]: { whole, decks } } for the shown date

  // Dashboard state (right column on desktop)
  const [dashData, setDashData]               = useState(null);
  const [allPending, setAllPending]           = useState([]);
  const [allBalance, setAllBalance]           = useState([]);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [showBalanceModal, setShowBalanceModal] = useState(false);

  const { settings: cruiseSettings } = useCruiseSettings();
  const capacitySlots = useMemo(() => cruiseSettings.flatMap(s => {
    const slots = [];
    if (s.has_upper_deck) slots.push({ cruise: s.cruise_name, deck: 'Upper Deck', max: DECK_CAPACITY['Upper Deck'] || 60 });
    if (s.has_middle_deck) slots.push({ cruise: s.cruise_name, deck: 'Middle Deck', max: DECK_CAPACITY['Middle Deck'] || 90 });
    return slots;
  }), [cruiseSettings]);

  const selectedDate = toDisplayDate(inputDate);
  const dateWords    = formatDateWords(inputDate);

  // Draft notification — once on mount
  useEffect(() => {
    supabase.from('draft_inquiries')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'unreviewed')
      .then(({ count }) => setDraftCount(count || 0));
  }, []);

  // Recently added bookings — independent of selected date, once on mount
  useEffect(() => {
    supabase.from('bookings')
      .select('booking_name, cruise_type, deck, date, adults, children, payment_status, created_at')
      .order('created_at', { ascending: false })
      .limit(5)
      .then(({ data }) => {
        const p = b => (b.adults || 0) + (b.children || 0);
        setRecentBookings((data || []).map(b => ({
          booking_name: b.booking_name,
          cruise_type:  b.cruise_type,
          deck:         b.deck,
          date:         b.date,
          pax:          p(b),
          payment_status: b.payment_status,
        })));
      });
  }, []);

  // Unified data fetch — capacity + dashboard metrics + this week
  const fetchData = useCallback(async (iDate) => {
    if (!iDate) return;
    setLoading(true);
    const dDate  = toDisplayDate(iDate);
    // This week is always the real current week, independent of selected date
    const today  = todayInput();
    const monday = getMondayOf(today);
    const weekDates = generateDisplayDates(monday, today);
    const weekInputDates = generateInputDates(monday, today);

    try {
      const [
        { data: todayBkgs },
        { data: chartersData },
        { data: weekBkgs },
        { data: pendingBkgs },
        { data: todayAdjData },
        { data: weekAdjData },
      ] = await Promise.all([
        supabase.from('bookings')
          .select('id, adults, children, total_amount, net_total, tac, payment_status, cruise_type, deck, payment_mode, razorpay_amount, cash_amount, icici_amount, bank_transfer_amount, gpay_amount, is_refunded, refund_amount')
          .eq('date', dDate),
        eventsOnDay(iDate),
        supabase.from('bookings')
          .select('id, adults, children, total_amount, tac, payment_status, partner_name, cruise_type, razorpay_amount, cash_amount, icici_amount, bank_transfer_amount, gpay_amount, is_refunded, refund_amount')
          .in('date', weekDates)
          .limit(2000),
        supabase.from('bookings')
          .select('id, booking_name, partner_name, date, cruise_type, net_total, payment_status, created_at, total_amount, tac, razorpay_amount, cash_amount, icici_amount, bank_transfer_amount, gpay_amount')
          .in('payment_status', ['Pending', 'Balance'])
          .order('date', { ascending: false })
          .limit(2000),
        supabase.from('daily_adjustments')
          .select('amount')
          .eq('date', iDate),
        supabase.from('daily_adjustments')
          .select('amount')
          .in('date', weekInputDates),
      ]);

      // Tracker entries (driver commission) for today's AND
      // this week's bookings — one query over the union of ids, split locally.
      const todayBkgIds = (todayBkgs || []).map(b => b.id).filter(Boolean);
      const weekBkgIds  = (weekBkgs || []).map(b => b.id).filter(Boolean);
      const allTrackerIds = [...new Set([...todayBkgIds, ...weekBkgIds])];
      let trackerRows = [];
      if (allTrackerIds.length > 0) {
        const { data: te } = await supabase
          .from('tracker_entries')
          .select('booking_id, driver_commission')
          .in('booking_id', allTrackerIds);
        trackerRows = te || [];
      }
      const todayIdSet = new Set(todayBkgIds);
      const weekIdSet  = new Set(weekBkgIds);
      const todayTrackerEntries = trackerRows.filter(t => todayIdSet.has(t.booking_id));
      const weekTrackerEntries  = trackerRows.filter(t => weekIdSet.has(t.booking_id));

      const pax = b => (b.adults || 0) + (b.children || 0);
      const received = b => ['razorpay_amount', 'cash_amount', 'icici_amount', 'bank_transfer_amount', 'gpay_amount']
        .reduce((s, k) => s + (Number(b[k]) || 0), 0);

      // ── Capacity ──
      const counts = {};
      capacitySlots.forEach(s => { counts[`${s.cruise}||${s.deck}`] = 0; });
      // boardingActive: excludes Cancelled (for pax/capacity counts — cancelled guests don't board)
      const boardingActive = (todayBkgs || []).filter(b => b.payment_status !== 'Cancelled' && b.payment_status !== 'FOC');
      // revenueActive: non-FOC, non-Cancelled — billed-side TAC, same row set as Reports' Total TAC
      const revenueActive  = (todayBkgs || []).filter(b => b.payment_status !== 'FOC' && b.payment_status !== 'Cancelled');
      // modeActive: non-FOC — the RECEIPTS row set (mode columns = money actually received, net of
      // TAC at source). Net Collection and its refunds both come from this one population — the
      // same definition as Reports / RangeReports, so the label never disagrees across screens.
      const modeActive = (todayBkgs || []).filter(b => b.payment_status !== 'FOC');
      // Keep 'active' as alias for boardingActive for capacity/pax/outstanding (existing behaviour)
      const active = boardingActive;
      active.forEach(b => {
        const key = `${b.cruise_type}||${b.deck}`;
        if (key in counts) counts[key] += pax(b);
      });
      setCapacityData(counts);
      setDayEvents(chartersData || []);

      // ── Today KPIs — same formula as Reports ──
      const totalPax = active.reduce((s, b) => s + pax(b), 0);
      const receivedTotal   = modeActive.reduce((s, b) => s + received(b), 0);
      const totalTAC        = revenueActive.reduce((s, b) => s + (b.tac || 0), 0);
      const driverCommission = todayTrackerEntries.reduce((s, te) => s + (Number(te.driver_commission) || 0), 0);
      const adjNet          = (todayAdjData || []).reduce((s, a) => s + (Number(a.amount) || 0), 0);
      const totalRefunds    = modeActive
        .filter(b => b.is_refunded && Number(b.refund_amount) > 0)
        .reduce((s, b) => s + (Number(b.refund_amount) || 0), 0);
      const netCollection   = receivedTotal - driverCommission + adjNet - totalRefunds;
      const outstandingNet =
        active.filter(b => b.payment_status === 'Pending').reduce((s, b) => s + (b.net_total || 0), 0) +
        active.filter(b => b.payment_status === 'Balance').reduce((s, b) => s + (b.net_total || 0), 0);

      // ── Pending (past and selected date only) ──
      const outstanding = (pendingBkgs || []).filter(b => isPastOrToday(b.date, iDate));
      const pendingList = outstanding.filter(b => b.payment_status === 'Pending');
      setAllPending(pendingList);
      // ── Balance: every Balance-status booking, any date, unpaid remainder — the same
      // rule as the Partner Ledger and the reports' all-dates figure, so they agree.
      const balanceList = (pendingBkgs || [])
        .filter(b => b.payment_status === 'Balance')
        .map(b => ({ ...b, remaining: ((Number(b.total_amount) || 0) - (Number(b.tac) || 0)) - received(b) }))
        .sort((a, b) => a.date.split('-').reverse().join('').localeCompare(b.date.split('-').reverse().join('')));
      setAllBalance(balanceList);
      const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 2);
      const pendingNet     = pendingList.reduce((s, b) => s + (b.net_total || 0), 0);
      const balanceNet     = balanceList.reduce((s, b) => s + b.remaining, 0);
      const oldPendingCount = pendingList.filter(b => new Date(b.created_at) < cutoff).length;

      // ── This week ──
      // weekModeActive: non-FOC — receipts row set for the week
      // weekRevenueActive: non-FOC, non-Cancelled — billed-side TAC (same set as Reports' Total TAC)
      // weekBoardingActive: for pax/partners (excludes both Cancelled and FOC)
      // weekNetCollection is the EXACT daily Net Collection formula summed over
      // Monday→today — payments received on the receipts row set, minus refunds
      // from that same set, minus driver commission (tracker entries for ALL the
      // week's bookings, as the daily figure does per date),
      // plus daily adjustments. The two figures share a label and MUST NOT disagree.
      const weekModeActive     = (weekBkgs || []).filter(b => b.payment_status !== 'FOC');
      const weekBoardingActive = weekModeActive.filter(b => b.payment_status !== 'Cancelled');
      const weekRevenueActive  = weekBoardingActive;
      const weekPax        = weekBoardingActive.reduce((s, b) => s + pax(b), 0);
      const weekReceived   = weekModeActive.reduce((s, b) => s + received(b), 0);
      const weekRefunds    = weekModeActive
        .filter(b => b.is_refunded && Number(b.refund_amount) > 0)
        .reduce((s, b) => s + (Number(b.refund_amount) || 0), 0);
      const weekDriverCommission = weekTrackerEntries.reduce((s, te) => s + (Number(te.driver_commission) || 0), 0);
      const weekAdjNet           = (weekAdjData || []).reduce((s, a) => s + (Number(a.amount) || 0), 0);
      const weekNetCollection = weekReceived - weekDriverCommission + weekAdjNet - weekRefunds;
      const weekTAC        = weekRevenueActive.reduce((s, b) => s + (b.tac || 0), 0);
      const partnerTAC = {};
      weekBoardingActive.forEach(b => { if (b.partner_name) partnerTAC[b.partner_name] = (partnerTAC[b.partner_name] || 0) + (b.tac || 0); });
      const allPartners = Object.entries(partnerTAC).sort((a, b) => b[1] - a[1]).map(([name, tac]) => ({ name, tac }));

      const weekCruisePax = {};
      weekBoardingActive.forEach(b => { if (b.cruise_type) weekCruisePax[b.cruise_type] = (weekCruisePax[b.cruise_type] || 0) + pax(b); });

      setDashData({ totalPax, netCollection, totalTAC, outstandingNet, pendingNet, balanceNet, oldPendingCount, weekPax, weekNetCollection, weekTAC, allPartners, weekCruisePax, monday, weekToDate: today });
    } finally {
      setLoading(false);
    }
  }, [capacitySlots]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(inputDate); }, [inputDate, fetchData]);

  // Online-booking closures for the shown date — READ ONLY, additive (does not touch
  // the capacity computation above). Loaded per cruise via the shared helper, which
  // resolves cruise_code and matches the DD-MM-YYYY date. Refresh on date change and
  // on window focus; no constant polling.
  const fetchClosures = useCallback(async (iDate) => {
    if (!iDate) { setClosures({}); return; }
    const dDate = toDisplayDate(iDate);
    const cruises = [...new Set(capacitySlots.map(s => s.cruise))];
    const results = await Promise.all(cruises.map(c => getCruiseClosures(c, dDate)));
    const map = {};
    cruises.forEach((c, i) => { map[c] = results[i]; });
    setClosures(map);
  }, [capacitySlots]);

  useEffect(() => { fetchClosures(inputDate); }, [inputDate, fetchClosures]);
  useEffect(() => {
    const onFocus = () => fetchClosures(inputDate);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [inputDate, fetchClosures]);

  const changeDay = (delta) => {
    const d = new Date(inputDate + 'T00:00:00');
    d.setDate(d.getDate() + delta);
    setInputDate(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`);
  };

  // The event occupying a cruise/deck slot that day, if any. Events never feed
  // the capacity number (that is bookings only); they only badge the card.
  // An event with no decks chosen takes the whole vessel.
  const getEventOnSlot = (cruise, deck) => {
    for (const ev of dayEvents) {
      const decks = ev.decks || [];
      if ((ev.cruise_types || []).includes(cruise) && (decks.length === 0 || decks.includes(deck) || decks.includes('Both')))
        return ev;
    }
    return null;
  };

  // ─── Shared summary content (desktop right col + mobile Summary tab) ────────

  const kpiCard = (label, value, color) => (
    <div key={label} style={{ background: '#fff', border: '1px solid var(--color-border)', borderRadius: 10, padding: '14px 16px', boxShadow: '0 1px 4px rgba(10,22,40,0.04)' }}>
      <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: '1.2rem', fontWeight: 800, color, letterSpacing: '-0.3px', wordBreak: 'break-all' }}>{value}</div>
    </div>
  );

  const summaryContent = dashData ? (
    <>
      {/* Today's Summary — 2×2 KPI grid */}
      <div style={{ background: '#fff', border: '1px solid var(--color-border)', borderRadius: 14, padding: '20px', marginBottom: 12, boxShadow: '0 1px 6px rgba(10,22,40,0.06)' }}>
        <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 14 }}>
          {inputDate === todayInput() ? "Today's Summary" : `Summary — ${dateWords}`}
          {loading && <span style={{ fontSize: '0.65rem', color: 'var(--color-ink-faint)', marginLeft: 8, fontWeight: 400 }}>Updating…</span>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {kpiCard('Total Pax',      dashData.totalPax,                                 NAVY)}
          {kpiCard('Net Collection', `₹${fmt(dashData.netCollection)}`,                 '#166534')}
          {kpiCard('TAC',            `₹${fmt(dashData.totalTAC)}`,                      '#92400e')}
          {kpiCard('Outstanding',    `₹${fmt(dashData.outstandingNet)}`,                dashData.outstandingNet > 0 ? '#dc2626' : '#6b7280')}
        </div>
      </div>

      {/* Pending + Balance clickable cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
        <button
          onClick={() => setShowPendingModal(true)}
          style={{ textAlign: 'left', cursor: 'pointer', background: allPending.length > 0 ? '#fffbeb' : '#fff', border: `1px solid ${allPending.length > 0 ? '#fcd34d' : 'var(--color-border)'}`, borderRadius: 12, padding: '14px 16px', boxShadow: '0 1px 4px rgba(10,22,40,0.04)', transition: 'border-color 0.15s, box-shadow 0.15s' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#f59e0b'; e.currentTarget.style.boxShadow = '0 3px 12px rgba(245,158,11,0.18)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = allPending.length > 0 ? '#fcd34d' : 'var(--color-border)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(10,22,40,0.04)'; }}
        >
          <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Pending</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: allPending.length > 0 ? '#92400e' : '#6b7280', lineHeight: 1 }}>{allPending.length}</div>
          <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#92400e', marginTop: 4 }}>₹{fmt(dashData.pendingNet)}</div>
          {dashData.oldPendingCount > 0 && <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#b45309', marginTop: 4 }}>{dashData.oldPendingCount} over 2 days</div>}
          <div style={{ fontSize: '0.65rem', color: GOLD, fontWeight: 700, marginTop: 8 }}>View →</div>
        </button>
        <button
          onClick={() => setShowBalanceModal(true)}
          style={{ textAlign: 'left', cursor: 'pointer', background: allBalance.length > 0 ? '#eff6ff' : '#fff', border: `1px solid ${allBalance.length > 0 ? '#93c5fd' : 'var(--color-border)'}`, borderRadius: 12, padding: '14px 16px', boxShadow: '0 1px 4px rgba(10,22,40,0.04)', transition: 'border-color 0.15s, box-shadow 0.15s' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#60a5fa'; e.currentTarget.style.boxShadow = '0 3px 12px rgba(96,165,250,0.18)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = allBalance.length > 0 ? '#93c5fd' : 'var(--color-border)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(10,22,40,0.04)'; }}
        >
          <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Balance</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: allBalance.length > 0 ? '#1e40af' : '#6b7280', lineHeight: 1 }}>{allBalance.length}</div>
          <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#1e40af', marginTop: 4 }}>₹{fmt(dashData.balanceNet)}</div>
          <div style={{ fontSize: '0.65rem', color: GOLD, fontWeight: 700, marginTop: 8 }}>View →</div>
        </button>
      </div>

      {/* This Week */}
      <div style={{ background: '#fff', border: '1px solid var(--color-border)', borderRadius: 14, padding: '20px', boxShadow: '0 1px 6px rgba(10,22,40,0.06)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.6px' }}>This Week</div>
          <div style={{ fontSize: '0.67rem', color: 'var(--color-ink-faint)' }}>{shortDate(dashData.monday)} – {shortDate(dashData.weekToDate)}</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 14 }}>
          {[
            { label: 'Pax',        value: dashData.weekPax },
            { label: 'Net Collection', value: `₹${fmt(dashData.weekNetCollection)}` },
            { label: 'TAC',        value: `₹${fmt(dashData.weekTAC)}` },
          ].map(({ label, value }) => (
            <div key={label} style={{ textAlign: 'center', background: 'var(--color-sunken)', borderRadius: 8, padding: '10px 6px' }}>
              <div style={{ fontSize: '0.6rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: '0.92rem', fontWeight: 800, color: NAVY, wordBreak: 'break-all' }}>{value}</div>
            </div>
          ))}
        </div>
        {Object.keys(dashData.weekCruisePax).length > 0 && (
          <div style={{ borderTop: '1px solid var(--color-border-subtle)', paddingTop: 12, marginBottom: 12 }}>
            <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'var(--color-ink-faint)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Pax by Cruise</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {Object.entries(dashData.weekCruisePax).sort((a, b) => b[1] - a[1]).map(([cruise, count]) => (
                <div key={cruise} style={{ background: 'var(--color-sunken)', borderRadius: 8, padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: '0.75rem', color: '#4a5a7a', fontWeight: 600 }}>{cruise}:</span>
                  <span style={{ fontSize: '0.75rem', fontWeight: 800, color: NAVY }}>{count} pax</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {dashData.allPartners.length > 0 && (
          <div style={{ borderTop: '1px solid var(--color-border-subtle)', paddingTop: 12 }}>
            <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'var(--color-ink-faint)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Partners by TAC</div>
            {(showAllPartners ? dashData.allPartners : dashData.allPartners.slice(0, 5)).map((p, i, arr) => (
              <div key={p.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: i < arr.length - 1 ? '1px solid var(--color-border-subtle)' : 'none' }}>
                <span style={{ fontSize: '0.8rem', color: '#4a5a7a', fontWeight: 600 }}>{p.name}</span>
                <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#92400e' }}>₹{fmt(p.tac)}</span>
              </div>
            ))}
            {dashData.allPartners.length > 5 && (
              <button onClick={() => setShowAllPartners(v => !v)} style={{ marginTop: 8, fontSize: '0.72rem', fontWeight: 700, color: GOLD, background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0' }}>
                {showAllPartners ? 'Show less ↑' : `View all ${dashData.allPartners.length} partners →`}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Recently Added Bookings */}
      {recentBookings.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid var(--color-border)', borderRadius: 14, padding: '20px', marginTop: 12, boxShadow: '0 1px 6px rgba(10,22,40,0.06)' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 14 }}>Recently Added Bookings</div>
          {recentBookings.map((b, i) => {
            const st = b.payment_status;
            const statusColor = st === 'Confirmed' ? '#15803d' : st === 'Pending' ? '#92400e' : st === 'Balance' ? '#1e40af' : st === 'Cancelled' ? '#dc2626' : '#6b7280';
            const statusBg    = st === 'Confirmed' ? '#f0fdf4'  : st === 'Pending' ? '#fffbeb'  : st === 'Balance' ? '#eff6ff'  : st === 'Cancelled' ? '#fff1f2'  : '#f9fafb';
            const [dd, mm, yy] = (b.date || '').split('-').map(Number);
            const dateLabel = b.date ? `${dd} ${MONTHS[mm - 1].slice(0, 3)} ${yy}` : '';
            return (
              <div key={i} onClick={() => onNavigate('view-bookings', b.date)}
                style={{ padding: '10px 0', borderBottom: i < recentBookings.length - 1 ? '1px solid var(--color-border-subtle)' : 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderRadius: 4, transition: 'background 0.12s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#fafaf8'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.88rem', fontWeight: 700, color: NAVY, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.booking_name}</div>
                  <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: 2 }}>{b.cruise_type}{b.deck ? ` · ${b.deck}` : ''} · {dateLabel} · {b.pax} pax</div>
                </div>
                <span style={{ flexShrink: 0, marginLeft: 10, fontSize: '0.7rem', fontWeight: 700, color: statusColor, background: statusBg, borderRadius: 6, padding: '2px 8px', whiteSpace: 'nowrap' }}>{b.payment_status}</span>
              </div>
            );
          })}
        </div>
      )}
    </>
  ) : null;

  // ─── Left column ─────────────────────────────────────────────────────────────

  const leftCol = (
    <>
      {/* Date picker */}
      <div style={{ background: '#fff', border: '1px solid var(--color-border)', borderRadius: 14, padding: '20px 24px', marginBottom: 16, boxShadow: '0 1px 6px rgba(10,22,40,0.06)' }}>
        <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Select Date</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => changeDay(-1)} style={{ width: 44, height: 44, background: NAVY, color: '#fff', border: 'none', borderRadius: 10, fontSize: '1.2rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background 0.15s' }} onMouseEnter={e => e.currentTarget.style.background = GOLD} onMouseLeave={e => e.currentTarget.style.background = NAVY} aria-label="Previous day">←</button>
          <input type="date" value={inputDate} onChange={e => setInputDate(e.target.value)} style={{ background: 'var(--color-sunken)', border: '1.5px solid var(--color-border-strong)', borderRadius: 8, padding: '10px 14px', color: NAVY, fontSize: '0.95rem', fontWeight: 600, outline: 'none', flex: 1, maxWidth: 200, colorScheme: 'light' }} />
          <button onClick={() => changeDay(1)} style={{ width: 44, height: 44, background: NAVY, color: '#fff', border: 'none', borderRadius: 10, fontSize: '1.2rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background 0.15s' }} onMouseEnter={e => e.currentTarget.style.background = GOLD} onMouseLeave={e => e.currentTarget.style.background = NAVY} aria-label="Next day">→</button>
        </div>
        {dateWords && <p style={{ marginTop: 10, fontSize: '1.35rem', fontWeight: 700, color: GOLD, letterSpacing: '-0.3px', marginBottom: 0 }}>{dateWords}</p>}
        {loading && <div style={{ fontSize: '0.7rem', color: 'var(--color-ink-faint)', marginTop: 6 }}>Updating…</div>}
      </div>

      {/* Mobile tab switcher — hidden on desktop via CSS */}
      <div className="home-tab-switcher">
        <button className={`home-tab-btn${mobileTab === 'capacity' ? ' active' : ''}`} onClick={() => setMobileTab('capacity')}>Capacity</button>
        <button className={`home-tab-btn${mobileTab === 'summary' ? ' active' : ''}`} onClick={() => setMobileTab('summary')}>Summary</button>
      </div>

      {/* Capacity pane — always visible on desktop, toggleable on mobile */}
      <div className={`home-tab-pane-capacity${mobileTab === 'capacity' ? ' active' : ''}`}>
        {/* Draft notification */}
        {draftCount > 0 && (
          <button onClick={() => onNavigate('draft-inquiries')} style={{ width: '100%', background: '#fffbeb', border: '1px solid #fcd34d', borderLeft: '4px solid #f59e0b', borderRadius: 12, padding: '13px 18px', marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', textAlign: 'left', boxShadow: '0 1px 6px rgba(10,22,40,0.06)' }}>
            <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#92400e', display: 'inline-flex', alignItems: 'center', gap: 6 }}><Mail size={16} strokeWidth={1.75} aria-hidden="true" /> {draftCount} unreviewed email {draftCount === 1 ? 'inquiry' : 'inquiries'}</span>
            <span style={{ fontSize: '0.8rem', color: '#b45309', fontWeight: 600, flexShrink: 0 }}>Review →</span>
          </button>
        )}

        {/* Capacity overview */}
        <div style={{ background: '#fff', border: '1px solid var(--color-border)', borderRadius: 14, padding: '20px 24px', marginBottom: 16, boxShadow: '0 1px 6px rgba(10,22,40,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <h2 style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', margin: 0 }}>Capacity Overview</h2>
            {loading && <span style={{ fontSize: '0.72rem', color: 'var(--color-ink-faint)' }}>Updating…</span>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {capacitySlots.map(slot => {
              const key = `${slot.cruise}||${slot.deck}`;
              const p   = capacityData[key] || 0;
              const pct = Math.min((p / slot.max) * 100, 100);
              const colors        = capacityColor(p, slot.max);
              const slotEvent     = getEventOnSlot(slot.cruise, slot.deck);
              const charterStatus = slotEvent ? (slotEvent.status === 'confirmed' || slotEvent.status === 'completed' ? 'Confirmed' : 'Pending') : null;
              const isHovered     = hoveredCard === key;

              // Online-booking closure overlay (additive — capacity numbers unchanged).
              // A whole-cruise closure (deck=NULL) reserves every deck of that cruise;
              // a deck closure reserves just this slot. Whole-cruise wins.
              const cl = closures[slot.cruise];
              const wholeClosed = cl?.whole || null;
              const deckClosed  = cl ? Object.prototype.hasOwnProperty.call(cl.decks, slot.deck) : false;
              if (wholeClosed || deckClosed) {
                const closedReason = wholeClosed ? wholeClosed.reason : cl.decks[slot.deck];
                const closedScope  = wholeClosed ? 'Cruise closed' : 'Deck reserved';
                return (
                  <div key={key}
                    style={{ background: '#eef2f7', border: '1px solid #94A3B8', borderLeft: '4px solid #64748B', borderRadius: 10, padding: '13px 16px' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.9rem', fontWeight: 700, color: NAVY }}>{slot.cruise}</span>
                        <span style={{ fontSize: '0.72rem', color: 'var(--color-ink-muted)', background: '#0A162210', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>{slot.deck}</span>
                      </div>
                      <span style={{ fontSize: '0.58rem', fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase', color: '#334155', background: '#CBD5E1', padding: '3px 9px', borderRadius: 4 }}>Reserved</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569' }}>{closedScope}</span>
                      <span style={{ fontSize: '0.72rem', color: '#475569', opacity: 0.85 }}>{closedReason || 'Closed for online booking'}</span>
                    </div>
                  </div>
                );
              }

              return (
                <div key={key}
                  onClick={() => onNavigate('add-inquiry-prefill', selectedDate, { cruise_type: slot.cruise, deck: slot.deck })}
                  onMouseEnter={() => setHoveredCard(key)}
                  onMouseLeave={() => setHoveredCard(null)}
                  style={{ background: colors.bg, border: `1px solid ${isHovered ? GOLD : colors.border}`, borderLeft: `4px solid ${colors.leftBar}`, borderRadius: 10, padding: '13px 16px', cursor: 'pointer', boxShadow: isHovered ? '0 4px 14px rgba(201,168,76,0.18)' : 'none', transition: 'box-shadow 0.18s, border-color 0.18s' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 9, flexWrap: 'wrap', gap: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.9rem', fontWeight: 700, color: NAVY }}>{slot.cruise}</span>
                      <span style={{ fontSize: '0.72rem', color: 'var(--color-ink-muted)', background: '#0A162210', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>{slot.deck}</span>
                      {charterStatus && (
                        <span style={{ background: charterStatus === 'Confirmed' ? '#C9A84C22' : '#f59e0b22', color: charterStatus === 'Confirmed' ? '#7a5a10' : '#92400e', border: `1px solid ${charterStatus === 'Confirmed' ? '#C9A84C88' : '#f59e0b88'}`, borderRadius: 20, padding: '2px 9px', fontSize: '0.68rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}><Anchor size={12} strokeWidth={1.75} aria-hidden="true" /> {typeLabel(slotEvent.event_type).toUpperCase()}</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                      {isHovered && <span style={{ fontSize: '0.72rem', fontWeight: 700, color: GOLD }}>+ Add Booking</span>}
                      <span style={{ fontSize: '0.9rem', fontWeight: 800, color: colors.label }}>{p} / {slot.max}</span>
                    </div>
                  </div>
                  <div style={{ background: '#0A162812', borderRadius: 6, height: 10, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: `linear-gradient(90deg, ${colors.bar}, ${colors.barEnd})`, borderRadius: 6, transition: 'width 0.5s ease' }} />
                  </div>
                  <div style={{ marginTop: 5, fontSize: '0.69rem', color: colors.label, opacity: 0.7, textAlign: 'right', fontWeight: 600 }}>{pct.toFixed(0)}% filled</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Online booking dates — open/closed per date from the public availability function */}
        <AvailabilityStrip />

        {/* Action buttons — 2 per row on mobile, 3 on desktop */}
        <div className="home-btn-grid">
          <button className="btn btn-gold"     onClick={() => onNavigate('add-inquiry', selectedDate)}   disabled={!inputDate}>+ Add Booking</button>
          <button className="btn btn-navy"     onClick={() => onNavigate('event-form', selectedDate, { event_type: 'private_charter', event_date: inputDate, returnTo: 'home', returnDate: selectedDate })} disabled={!inputDate}>Add Private Charter</button>
        </div>
      </div>

      {/* Summary pane — mobile only, always hidden on desktop via CSS */}
      <div className={`home-tab-pane-summary${mobileTab === 'summary' ? ' active' : ''}`}>
        {summaryContent}
      </div>
    </>
  );

  // ─── Right column (desktop only) ─────────────────────────────────────────────

  const rightCol = summaryContent;

  // ─── Modals ──────────────────────────────────────────────────────────────

  const modalClose = (setter) => () => setter(false);

  const PendingModal = showPendingModal && (
    <div className="ss-modal-backdrop" onClick={modalClose(setShowPendingModal)}>
      <div className="ss-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 800, color: NAVY, margin: 0 }}>Pending Bookings ({allPending.length})</h3>
          <button onClick={modalClose(setShowPendingModal)} style={{ background: 'none', border: 'none', fontSize: '1.1rem', cursor: 'pointer', color: '#6b7280', padding: '2px 6px' }}>✕</button>
        </div>
        {allPending.length === 0 ? (
          <p style={{ fontSize: '0.85rem', color: 'var(--color-ink-muted)', textAlign: 'center', padding: '20px 0' }}>No pending bookings</p>
        ) : (
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {allPending.map(b => {
              const days = daysSince(b.created_at);
              return (
                <div key={b.id} style={{ padding: '11px 0', borderBottom: '1px solid var(--color-border-subtle)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontSize: '0.88rem', fontWeight: 700, color: NAVY }}>{b.booking_name}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 2 }}>{b.date} · {b.cruise_type}</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#92400e' }}>₹{fmt(b.net_total)}</div>
                      <div style={{ fontSize: '0.7rem', fontWeight: 600, color: days > 2 ? '#b45309' : '#6b7280', marginTop: 2 }}>
                        {days === 0 ? 'Today' : days === 1 ? '1 day ago' : `${days} days ago`}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={modalClose(setShowPendingModal)} style={{ padding: '8px 20px', background: NAVY, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>Close</button>
        </div>
      </div>
    </div>
  );

  const BalanceModal = showBalanceModal && (
    <div className="ss-modal-backdrop" onClick={modalClose(setShowBalanceModal)}>
      <div className="ss-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 800, color: NAVY, margin: 0 }}>Balance Bookings ({allBalance.length})</h3>
          <button onClick={modalClose(setShowBalanceModal)} style={{ background: 'none', border: 'none', fontSize: '1.1rem', cursor: 'pointer', color: '#6b7280', padding: '2px 6px' }}>✕</button>
        </div>
        {allBalance.length === 0 ? (
          <p style={{ fontSize: '0.85rem', color: 'var(--color-ink-muted)', textAlign: 'center', padding: '20px 0' }}>No balance bookings</p>
        ) : (
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {allBalance.map(b => {
              const [bDay, bMon, bYr] = (b.date || '').split('-').map(Number);
              const cruiseDate = new Date(bYr, bMon - 1, bDay);
              const today = new Date(); today.setHours(0, 0, 0, 0);
              const daysPending = Math.max(0, Math.floor((today - cruiseDate) / 86400000));
              return (
                <div key={b.id}
                  onClick={() => { setShowBalanceModal(false); onNavigate('daily-tracker', b.date, { cruise_type: b.cruise_type }); }}
                  style={{ padding: '11px 0', borderBottom: '1px solid var(--color-border-subtle)', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--color-sunken)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ minWidth: 0, paddingRight: 12 }}>
                      <div style={{ fontSize: '0.88rem', fontWeight: 700, color: NAVY }}>{b.booking_name}</div>
                      {/* Partner line only when there is one — direct guests get no placeholder row */}
                      {b.partner_name && (
                        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1e40af', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.partner_name}</div>
                      )}
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 2 }}>{b.date} · {b.cruise_type}</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1e40af' }}>₹{fmt(b.remaining)}</div>
                      {b.remaining < ((Number(b.total_amount) || 0) - (Number(b.tac) || 0)) - 0.5 && (
                        <div style={{ fontSize: '0.68rem', color: '#6b7280', marginTop: 2 }}>of ₹{fmt((Number(b.total_amount) || 0) - (Number(b.tac) || 0))}</div>
                      )}
                      {daysPending > 0 && (
                        <div style={{ fontSize: '0.68rem', fontWeight: 700, color: daysPending > 2 ? '#dc2626' : '#92400e', marginTop: 2 }}>
                          {daysPending}d pending
                        </div>
                      )}
                      <div style={{ fontSize: '0.68rem', color: GOLD, fontWeight: 700, marginTop: 2 }}>Open →</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={modalClose(setShowBalanceModal)} style={{ padding: '8px 20px', background: NAVY, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>Close</button>
        </div>
      </div>
    </div>
  );

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div>
      <div className="home-layout">
        <div className="home-left">{leftCol}</div>
        <div className="home-right">{rightCol}</div>
      </div>

      {PendingModal}
      {BalanceModal}
    </div>
  );
}
